import { Link } from "react-router-dom";
import {
    HouseholdTrackingEligibility,
    HouseholdTrackingGroup,
    HouseholdTrackingSummary as SummaryModel
} from "../../models/pageModels/HouseholdTracking";
import "./HouseholdTracking.css";

interface HouseholdTrackingSummaryProps {
    summary: SummaryModel;
    groups: HouseholdTrackingGroup[];
    eligibility: Record<string, HouseholdTrackingEligibility>;
}

interface IneligiblePerson {
    key: string;
    clientId?: string;
    name: string;
    relationships: string[];
    reason: string;
}

const HouseholdTrackingSummary = (props: HouseholdTrackingSummaryProps) => {
    const ineligiblePeople = new Map<string, IneligiblePerson>();

    props.groups.forEach(group => {
        group.people.forEach(person => {
            const eligibility = person.clientId
                ? props.eligibility[person.clientId]
                : undefined;
            if (person.clientId && eligibility?.status !== 'ineligible') {
                return;
            }

            const key = person.clientId || person.key;
            const relationship = `${group.name}: ${person.relationship}`;
            const existing = ineligiblePeople.get(key);
            if (existing) {
                if (!existing.relationships.includes(relationship)) {
                    existing.relationships.push(relationship);
                }
                return;
            }

            ineligiblePeople.set(key, {
                key,
                clientId: person.clientId,
                name: person.name,
                relationships: [relationship],
                reason: eligibility?.reason || 'Not linked to a participant profile'
            });
        });
    });

    return (
        <div id="household-tracking-summary" className="household-tracking-card margin-top-4" aria-live="polite">
            <div className="household-tracking-card-header">
                <h4>Submission Summary</h4>
            </div>
            <div className="household-tracking-stats">
                <div className="household-tracking-stat">
                    <span>Total People</span>
                    <strong>{props.summary.totalPeopleReviewed}</strong>
                </div>
                <div className="household-tracking-stat">
                    <span>Selected</span>
                    <strong>{props.summary.eligibleParticipants}</strong>
                </div>
                <div className="household-tracking-stat">
                    <span>Not Eligible</span>
                    <strong>{props.summary.ineligible}</strong>
                </div>
                <div className="household-tracking-stat success">
                    <span>Successful Submissions</span>
                    <strong>{props.summary.succeeded}</strong>
                </div>
                <div className="household-tracking-stat error">
                    <span>Failed Submissions</span>
                    <strong>{props.summary.failed}</strong>
                </div>
            </div>
            <div className="household-tracking-table-container margin-top-4">
                <table className="household-tracking-table household-tracking-summary-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Household / Relationship</th>
                            <th>Status</th>
                            <th>Attempts</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.summary.results.map(result => (
                            <tr key={result.clientId}>
                                <td data-label="Name">
                                    <div className="text-bold">
                                        <Link to={`/participants/${result.clientId}/profile-demographics`} target="_blank">{result.name}</Link>
                                    </div>
                                    {result.isPrimary && <small>Current participant</small>}
                                </td>
                                <td data-label="Household / Relationship">
                                    {result.householdRelationships.length > 0
                                        ? result.householdRelationships.map(relationship => <div key={relationship}>{relationship}</div>)
                                        : <div>Current participant</div>}
                                </td>
                                <td data-label="Status">
                                    <div className={`household-tracking-status ${result.status === 'succeeded' ? 'eligible' : 'ineligible'}`}>
                                        {result.status === 'succeeded' ? 'Submitted' : 'Failed'}
                                    </div>
                                </td>
                                <td data-label="Attempts">{result.attempts}</td>
                                <td data-label="Details">
                                    {result.error
                                        ? <span className="household-tracking-reason">{result.error}</span>
                                        : <span className="text-sub">-</span>}
                                </td>
                            </tr>
                        ))}
                        {Array.from(ineligiblePeople.values()).map(person => (
                            <tr key={`ineligible-${person.key}`}>
                                <td data-label="Name" className="text-bold">
                                    {person.clientId
                                        ? <Link to={`/participants/${person.clientId}/profile-demographics`} target="_blank">{person.name}</Link>
                                        : person.name}
                                </td>
                                <td data-label="Household / Relationship">
                                    {person.relationships.map(relationship => <div key={relationship}>{relationship}</div>)}
                                </td>
                                <td data-label="Status">
                                    <div className="household-tracking-status ineligible">Not Eligible</div>
                                </td>
                                <td data-label="Attempts">-</td>
                                <td data-label="Details">
                                    <span className="household-tracking-reason">{person.reason}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default HouseholdTrackingSummary;
